<template>
  <div class="pc-attention">
    <header class="pc-attention__hero">
      <img
        class="pc-attention__hero-bg"
        src="@/assets/image/attention/page_bg.webp"
        alt="Attention page background"
        width="1920"
        height="1036"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-attention__kv"
        src="@/assets/image/attention/header_bg.webp"
        alt="Decorative header background illustration for the attention page"
        width="1677"
        height="1222"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-attention__title-img"
        src="@/assets/image/attention/title.webp"
        alt="Important notices"
        width="816"
        height="auto"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-attention__deco pc-attention__deco--left"
        src="@/assets/image/attention/nav_icon_left.webp"
        alt="Decorative graphic (left)"
        width="360"
        height="auto"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-attention__deco pc-attention__deco--right"
        src="@/assets/image/attention/nav_icon_right.webp"
        alt="Decorative graphic (right)"
        width="300"
        height="auto"
        decoding="async"
        loading="lazy"
      />

      <div class="pc-attention__marquee pc-attention__marquee--top">
        <div class="pc-attention__marquee-bg">
          <div class="pc-attention__marquee-bar">
            <div class="pc-attention__marquee-track">
              <div
                v-for="dup in 2"
                :key="'attention-marquee-' + dup"
                class="pc-attention__marquee-chunk"
                :aria-hidden="dup === 2 ? 'true' : undefined"
              >
                <img
                  v-for="n in 10"
                  :key="`attention-marquee-${dup}-${n}`"
                  class="pc-attention__marquee-img"
                  src="@/assets/image/attention/header_footer_title.webp"
                  width="314"
                  height="30"
                  alt="Decorative scrolling title ribbon (repeated)"
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pc-attention__content">
        <div
          v-for="(item, index) in attentionSections"
          :key="'attention-section-' + index"
          class="pc-attention__content-item"
        >
          <div
            class="pc-attention__content-item-title"
            :class="{ 'is-active': openIndex === index }"
            @click="openItem(index)"
          >
            <span>{{ item.title }}</span>
            <span class="pc-attention__content-item-toggle" aria-hidden="true">
              <img
                class="pc-attention__content-item-toggle-img pc-attention__content-item-toggle-img--open"
                src="@/assets/image/attention/open.webp"
                alt="Expand item"
                width="24"
                height="24"
              />
              <img
                class="pc-attention__content-item-toggle-img pc-attention__content-item-toggle-img--close"
                src="@/assets/image/attention/close.webp"
                alt="Collapse item"
                width="24"
                height="24"
              />
            </span>
          </div>
          <div
            class="pc-attention__content-item-body"
            :class="{ 'is-open': openIndex === index }"
          >
            <div class="pc-attention__content-item-body-inner">
              <div class="pc-attention__content-item-content">
                <div
                  v-for="(line, lineIdx) in item.content"
                  :key="index + '-line-' + lineIdx"
                  class="pc-attention__content-item-content-item"
                >
                  <template v-if="line === ''">
                    <br />
                  </template>
                  <template v-else-if="line.startsWith('・') || line.startsWith('※')">
                    <span class="pc-attention__content-item-text hanging-indent" v-html="line"></span>
                  </template>
                  <template v-else>
                    <span class="pc-attention__content-item-text" v-html="line"></span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>

<script>
import attentionContent from "@/views/data/attention-content.json";
import contentVisibility from "@/views/data/content-visibility.json";

/**
 * 注意事项页：手风琴文案维护 attention-content.json（每项 title + content 条文数组；图片在模板写死）
 */
export default {
  name: "PcAttentionPage",

  data() {
    return {
      // ========== 运营 / 页面配置（优先维护）==========
      attentionSectionsSource: attentionContent.attentionSections,

      // ========== 运行时状态（一般无需改）==========
      /** 当前展开的面板下标，null 表示全部收起 */
      openIndex: null,
    };
  },

  computed: {
    /**
     * content-visibility.json → attention
     * 根据配置控制祝い花与グッズ条目（双端一致）
     */
    attentionSections() {
      const visibility = contentVisibility.attention || {};
      return this.attentionSectionsSource.filter((item) => {
        if (item.title === "祝い花(スタンド花／楽屋花)・プレゼントについて") {
          return visibility.showFlowerAndPresentSection !== false;
        }
        if (item.title === "グッズについて") {
          return visibility.showGoodsSection !== false;
        }
        return true;
      });
    },
  },

  methods: {
    openItem(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
  },
};
</script>

<style lang="scss" scoped>
.pc-attention {
  background-color: #3c3068;
  box-sizing: border-box;
  text-align: left;

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  &__hero {
    position: relative;
    width: 100%;
    padding-top: 500px;
    box-sizing: border-box;
    background-image: url("@/assets/image/ticket/nav_bg.webp");
    background-size: 100% auto;
    background-repeat: no-repeat;
    border-bottom: 2px solid #9f77bb;

    .pc-attention__hero-bg {
      position: absolute;
      top: 0;
      left: 50%;
      width: 100vw;
      max-width: none;
      height: 1036px;
      transform: translateX(-50%);
      object-fit: cover;
      object-position: center;
    }

    .pc-attention__kv {
      position: absolute;
      top: 52px;
      left: 115px;
      width: 1677px;
      height: 1222px;
      object-fit: contain;
      object-position: center;
      z-index: -1;
    }

    .pc-attention__title-img {
      position: absolute;
      top: 179px;
      left: 552px;
      width: 816px;
      object-fit: contain;
      object-position: center;
    }

    .pc-attention__deco {
      position: absolute;
      object-fit: contain;
      object-position: center;
      transform-origin: center center;
      will-change: transform;
      animation: pc-attention-deco-beat 1.35s infinite;

      &--left {
        top: 52px;
        left: 115px;
        width: 360px;
      }

      &--right {
        top: 4px;
        right: 176px;
        width: 300px;
      }
    }

    .pc-attention__marquee {
      position: absolute;
      left: 0;
      width: 100%;
      height: 134px;
      padding-top: 1px;
      box-sizing: border-box;
      z-index: 10;

      &--top {
        top: 306px;
      }
    }

    .pc-attention__marquee-bg {
      padding-top: 45px;
      position: relative;
      width: 100%;
      height: 100%;
      background-image: url("@/assets/image/attention/header_footer_title_bg.webp");
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }

    .pc-attention__marquee-bar {
      overflow: hidden;
      background-color: #191616;
      height: 44px;
      display: flex;
      align-items: center;
      border-top: 1px solid #36e9ff;
      border-bottom: 1px solid #36e9ff;
    }

    .pc-attention__marquee-track {
      display: flex;
      width: max-content;
      flex-shrink: 0;
      align-items: center;
      animation: pc-attention-marquee 45s linear infinite;
      will-change: transform;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        will-change: auto;
      }
    }

    .pc-attention__marquee-chunk {
      display: flex;
      flex-shrink: 0;
      align-items: center;
    }

    .pc-attention__marquee-img {
      flex-shrink: 0;
      margin-right: 30px;
      width: 314px;
      height: 30px;
      object-fit: contain;
      object-position: center;
    }
  }

  .pc-attention__content {
    position: relative;
    z-index: 10;
    min-height: 800px;
    padding-bottom: 100px;
    &-item {
      margin: 0 auto 24px;
      padding: 0 40px;
      width: 1000px;
      background-color: #1c192b;
      border-radius: 8px;
      &-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 0;
        font-size: 18px;
        font-weight: 700;
        color: #fff380;
        cursor: pointer;
        user-select: none;
      }

      &-toggle {
        position: relative;
        flex-shrink: 0;
        width: 24px;
        height: 24px;
      }

      &-toggle-img {
        position: absolute;
        inset: 0;
        width: 24px;
        height: 24px;
        object-fit: contain;
        object-position: center;
        transition: opacity 0.3s ease,
          transform 0.35s cubic-bezier(0.33, 1, 0.32, 1);
      }

      &-toggle-img--close {
        opacity: 0;
        transform: rotate(-90deg) scale(0.85);
        pointer-events: none;
      }

      &-toggle-img--open {
        opacity: 1;
        transform: rotate(0deg) scale(1);
      }

      &-title.is-active {
        .pc-attention__content-item-toggle-img--open {
          opacity: 0;
          transform: rotate(90deg) scale(0.85);
        }

        .pc-attention__content-item-toggle-img--close {
          opacity: 1;
          transform: rotate(0deg) scale(1);
          pointer-events: none;
        }
      }

      &-body {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.42s cubic-bezier(0.33, 1, 0.32, 1);

        &.is-open {
          grid-template-rows: 1fr;
        }
      }

      &-body-inner {
        overflow: hidden;
        min-height: 0;
      }

      /* 不叠 opacity：scoped 下嵌套选择器易匹配失败导致正文一直透明，仅靠 grid 收合即可 */
      &-content {
        padding: 10px 0 20px;
        font-size: 14px;
        font-weight: 400;
        line-height: 2;
        color: #fff;
        border-top: 1px dashed #fff;
      }

      &-content-item {
        display: flex;
        align-items: flex-start;

        &-bullet {
          flex-shrink: 0;
          margin-right: 8px;
        }

        &-text {
          flex: 1;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        &-toggle-img {
          transition: none;
        }

        &-body {
          transition: none;
        }
      }
    }
  }
}

@keyframes pc-attention-deco-beat {
  0%,
  42% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  52% {
    transform: scale(0.9);
    animation-timing-function: cubic-bezier(0.55, 0.05, 0.6, 0.45);
  }
  58% {
    transform: scale(0.86);
    animation-timing-function: cubic-bezier(0.33, 1.15, 0.52, 1);
  }
  70% {
    transform: scale(1.08);
    animation-timing-function: cubic-bezier(0.22, 0.85, 0.38, 1);
  }
  80% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pc-attention-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
